import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTimes,
  FaBuilding,
  FaFingerprint,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaBriefcase,
  FaPercentage,
  FaSpinner,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const CompleteProfile = ({ isOpen, onProfileCompleted, user }) => {
  const navigate = useNavigate();

  // =========================================================
  // FORM STATE
  // =========================================================
  const [formData, setFormData] = useState({
    name: user?.fullName || "",
    tin: user?.tenantTin || "",
    phone: user?.tenantPhone || "",
    vat: user?.tenantVat || "",
    address: user?.tenantAddress || "",
    woreda: user?.woreda || "",
    industry: user?.tenantIndustry || "PHARMACY",
    fiscalType: user?.tenantFiscalType || "MANUAL",
    logoUrl: user?.tenantLogoUrl || "",
    tassPassword: user?.tassPassword || "",
  });

  // =========================================================
  // REGION STATE
  // =========================================================
  const [region, setRegion] = useState(user?.region || null);
  const [regions, setRegions] = useState([]);

  // =========================================================
  // UI STATE
  // =========================================================
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // =========================================================
  // LOAD REGIONS
  // =========================================================
  useEffect(() => {
    const loadRegions = async () => {
      try {
        setIsLoading(true);

        const response = await apiRequest("tenants/regions", {
          method: "GET",
        });

        // Adjust this if your API returns { data: [...] }
        const regionData = response?.data || response || [];

        setRegions(regionData);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        setError("Failed to load regions.");
      } finally {
        setIsLoading(false);
      }
    };

    loadRegions();
  }, []);

  // =========================================================
  // INPUT CHANGE
  // =========================================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // REGION CHANGE
  // =========================================================
  const handleRegionChange = (e) => {
    const selectedId = e.target.value;

    if (!selectedId) {
      setRegion(null);
      return;
    }

    const selectedRegion = regions.find((r) => r.id.toString() === selectedId);

    setRegion(selectedRegion || null);
  };

  // =========================================================
  // SUBMIT
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setError("");

    // Front-end validation
    if (
      !formData.name.trim() ||
      !formData.tin.trim() ||
      !formData.phone.trim() ||
      !formData.tassPassword.trim()
    ) {
      setError(
        "Business Name, TIN, Phone Number and Password are strictly required."
      );

      setIsLoading(false);
      return;
    }

    try {
      // =====================================================
      // BUILD REQUEST PAYLOAD
      // =====================================================
      const payload = {
        ...formData,

        region: region
          ? {
              id: region.id,
            }
          : null,
      };

      console.log("Complete profile payload:", payload);

      // =====================================================
      // API REQUEST
      // =====================================================
      const response = await apiRequest("tenants/completeProfile", {
        method: "POST",
        body: payload,
      });

      if (response?.success && response?.data) {
        const loginResponse = response.data;

        // Re-seed client context with the newly signed JWT payload
        localStorage.setItem("token", loginResponse.token);
        localStorage.setItem("user", JSON.stringify(loginResponse));

        // Notify parent
        if (onProfileCompleted) {
          onProfileCompleted(response);
        }
      } else {
        throw new Error(response?.message || "Profile update aborted.");
      }
    } catch (err) {
      console.error("Complete profile error:", err);

      setError(
        err?.response?.message || err?.message || "Failed to update profile."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================
  // LOGOUT
  // =========================================================
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    navigate("/");
  };

  // =========================================================
  // STYLES
  // =========================================================
  const inputClass =
    "w-full bg-slate-950/50 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-xs font-mono placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition";

  const labelClass =
    "block text-[11px] font-bold tracking-wider text-slate-400 uppercase mb-1.5";

  // =========================================================
  // DON'T RENDER
  // =========================================================
  if (!isOpen) return null;

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md z-[2000] flex items-center justify-center p-4 overflow-y-auto">
      {/* Glassmorphism Container */}
      <div className="relative w-full max-w-xl overflow-hidden border bg-white/10 dark:bg-slate-900/30 border-white/20 rounded-3xl shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 duration-200">
        {/* Background Effects */}
        <div className="absolute -top-24 -left-24 w-52 h-52 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="absolute -bottom-24 -right-24 w-52 h-52 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Branding Accent Strip */}
        <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" />

        <div className="p-6 sm:p-8 max-h-[90vh] overflow-y-auto custom-scrollbar">
          {/* =================================================
              HEADER
          ================================================= */}
          <div className="text-center space-y-2 mb-6">
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight uppercase">
              {user?.tenantName}
            </h2>

            <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
              Please complete your business profile.
            </p>
          </div>

          {/* =================================================
              ERROR
          ================================================= */}
          {error && (
            <div className="mb-5 rounded-xl border border-red-500/20 bg-red-950/30 p-3.5 text-center">
              <p className="text-xs text-red-400 font-semibold">{error}</p>
            </div>
          )}

          {/* =================================================
              FORM
          ================================================= */}
          <form onSubmit={handleSubmit} className="space-y-4 font-sans">
            {/* =================================================
                BUSINESS NAME
            ================================================= */}
            <div>
              <label className={labelClass}>Business Name *</label>

              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                  <FaBuilding className="text-xs" />
                </span>

                <input
                  type="text"
                  name="name"
                  placeholder="e.g., ABC PHARMACY PLC"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            {/* =================================================
                TIN & VAT
            ================================================= */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* TIN */}
              <div>
                <label className={labelClass}>TIN *</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <FaFingerprint className="text-xs" />
                  </span>

                  <input
                    type="text"
                    name="tin"
                    placeholder="e.g., 0012345678"
                    value={formData.tin}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              {/* VAT */}
              <div>
                <label className={labelClass}>VAT Number (Optional)</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <FaPercentage className="text-xs" />
                  </span>

                  <input
                    type="text"
                    name="vat"
                    placeholder="e.g., 002123456"
                    value={formData.vat}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* =================================================
                PHONE & REGION
            ================================================= */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* PHONE */}
              <div>
                <label className={labelClass}>Telephone *</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <FaPhoneAlt className="text-xs" />
                  </span>

                  <input
                    type="tel"
                    name="phone"
                    placeholder="e.g., +251911223344"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              {/* REGION */}
              <div>
                <label className={labelClass}>Region</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500 pointer-events-none">
                    <FaMapMarkerAlt className="text-xs" />
                  </span>

                  <select
                    value={region?.id || ""}
                    onChange={handleRegionChange}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="">Select Region</option>

                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* =================================================
                WOREDA & INDUSTRY
            ================================================= */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* WOREDA */}
              <div>
                <label className={labelClass}>Woreda</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <FaMapMarkerAlt className="text-xs" />
                  </span>

                  <input
                    type="text"
                    name="woreda"
                    placeholder="e.g. 11"
                    value={formData.woreda}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* INDUSTRY */}
              <div>
                <label className={labelClass}>Industry Type</label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                    <FaBriefcase className="text-xs" />
                  </span>

                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={`${inputClass} appearance-none pr-8 font-sans font-medium`}
                  >
                    <option value="PHARMACY">Pharmacy / Healthcare</option>

                    <option value="RETAIL">Retail Commerce</option>

                    <option value="WHOLESALE">Distribution & Wholesale</option>
                  </select>
                </div>
              </div>
            </div>

            {/* =================================================
                PHYSICAL ADDRESS
            ================================================= */}
            <div>
              <label className={labelClass}>Office Address</label>

              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                  <FaMapMarkerAlt className="text-xs" />
                </span>

                <input
                  type="text"
                  name="address"
                  placeholder="e.g., Bole Sub-City, Woreda 03, Addis Ababa"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>

            {/* FISCALIZATION MODE */}
            <div>
              <label className={labelClass}>Fiscalization Mode</label>

              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                  <FaCheckCircle className="text-xs" />
                </span>

                <select
                  name="fiscalType"
                  value={formData.fiscalType}
                  onChange={handleInputChange}
                  className={`${inputClass} appearance-none pr-8 font-sans font-medium`}
                >
                  <option value="MANUAL">Manual</option>
                  <option value="MACHINE">Machine</option>
                  <option value="EIMS">EIMS</option>
                </select>
              </div>
            </div>

            {/* =================================================
                PASSWORD
            ================================================= */}
            <div>
              <label className={labelClass}>Login Password *</label>

              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                  <FaFingerprint className="text-xs" />
                </span>

                <input
                  required
                  type="password"
                  name="tassPassword"
                  placeholder="*******"
                  value={formData.tassPassword}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>

            {/* =================================================
                LOGO URL
            ================================================= */}
            <div>
              <label className={labelClass}>Logo Link</label>

              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500">
                  <FaBuilding className="text-xs" />
                </span>

                <input
                  type="text"
                  name="logoUrl"
                  placeholder="e.g., https://abc.com/logo.png"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>

            {/* =================================================
                INFORMATION
            ================================================= */}
            <div className="p-3.5 rounded-2xl bg-slate-950/40 border border-white/5 text-[11px] text-slate-400 leading-normal">
              <span className="text-emerald-400 font-bold">Registry Rule:</span>{" "}
              Completing onboarding for approval processing. Ensure entered data
              are accurate.
            </div>

            {/* =================================================
                ACTION BUTTONS
            ================================================= */}
            <div className="pt-4 flex flex-col sm:flex-row justify-end gap-3">
              {/* CANCEL */}
              <button
                type="button"
                onClick={handleLogout}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 active:scale-95 text-slate-700 font-bold rounded-xl text-xs transition uppercase tracking-wider border border-slate-200"
                title="Cancel"
              >
                <FaTimes className="text-sm" />
                Cancel
              </button>

              {/* SUBMIT */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 active:scale-95 disabled:opacity-50 disabled:pointer-events-none text-white font-bold rounded-xl text-xs transition shadow-lg shadow-emerald-600/20 uppercase tracking-wider"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin text-sm" />
                    Synchronizing Profile...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="text-sm" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
